import {
  BooleanCellData,
  DEFAULT_FORMAT_OPTIONS,
  DossDataFormat,
  FormulaInput,
  FormulaValueType,
  getFormulaOutputType,
  getNextOrder,
  isFormulaType as isAnyFormulaType,
  isValidArrayInnerType,
  isValidFirstTableColumnType,
  LogicExpressionInput,
  titleCase,
} from '@doss/common-all'
import { TooltipTrigger } from '@radix-ui/react-tooltip'
import { FormikHelpers, useFormikContext } from 'formik'
import { compact, isNull, isUndefined, minBy, uniq, uniqBy } from 'lodash'
import { ComponentProps, ReactElement, useEffect, useMemo, useState } from 'react'
import { configure } from 'react-hotkeys'
import { useHotkeys } from 'react-hotkeys-hook'
/* oxlint-disable react-compiler/react-compiler */
// https://www.notion.so/doss-com/React-Compiler-Warning-Fixes-Log-34cf16c5ee36803c9817d41bdec112e1?source=copy_link
import { Divider } from '@/components/ui/divider'
import { Tooltip, TooltipContent } from '@/components/ui/tooltip'
import { InputWrapper } from '~/@/components/inputs/base/input-wrapper'
import { Dropdown } from '~/@/components/inputs/selection/dropdown'
import { Checkbox } from '~/@/components/inputs/standalone/checkbox'
import { LongTextInput } from '~/@/components/inputs/text/long-text-input'
import { ShortTextInput } from '~/@/components/inputs/text/short-text-input'
import { Option } from '~/@/components/selector'
import { Button } from '~/@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/@/components/ui/tabs'
import {
  ComprehensionEntityType,
  DataType,
  FormatPreset,
  GetDetailedTableQuery,
  IndexType,
} from '~/common/generated/apollo-operations'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { checkFormikDirtyState } from '~/common/utils/formik'
import { localKeyMap } from '~/common/utils/keymap'
import { getIconByColumnType } from '~/common/utils/table'
import {
  ALLOWED_ARRAY_INNER_TYPES,
  AllowedArrayInnerTypes,
  getHumanReadableTableColumnDataType,
} from '~/common/utils/table-column'
import { OpenDependencyExplorerButton } from '../dependency-explorer/OpenDependencyExplorerButton'
import { DefaultValueContext, DefaultValueField } from '../shared/DefaultValueField'
import { ORDERED_OPTIONS, orderLookup, RightPanelOperation } from '../table-settings/constants'
import { useTableColumnForm } from '../table-settings/TableColumnFormProvider'
import { RightPanelState } from '../table-settings/types'
import { SettingsPageTable } from '../tables/types'
import { LoadingSpinner } from '../ui/atoms/loading-spinner/LoadingSpinner'
import { DateFormatSection } from './DateFormatSection'
import { enumOptionLabels } from './enum-option-form-values'
import { EnumOptionsEditor } from './EnumOptionsEditor'
import { FieldPermissionsTab } from './FieldPermissionsTab'
import { tableColumnsToFieldReferences } from './formula/conversionUtils'
import { FormulaBuilder } from './formula/FormulaBuilder'
import { LookupColumnFields } from './lookup/LookupColumnFields'
import { NumberFormatSection } from './NumberFormatSection'
import { RelatedActionStepsView } from './RelatedActionStepsView'
import { ColumnFormValues, DEFAULT_COLUMN_VALUES, RelatedActionSteps } from './types'

export type TableColumn = GetDetailedTableQuery['table']['columns'][number]

type CreateUpdateDialogField = {
  element: ReactElement<ComponentProps<typeof ShortTextInput | typeof Dropdown | typeof Checkbox>>
}

interface CreateTableColumnFormProps {
  editingColumn?: ColumnFormValues | null
  table: Pick<SettingsPageTable, 'id' | 'columns' | 'hasRows'>
  parentTableId?: string | null
  order?: number
  setFormulaError: (error: string | null) => void
  formulaError: string | null
  operation: RightPanelOperation
  setRightPanelState?: (panelState: RightPanelState | null) => void
  showButtons?: boolean
  setHasUnsavedChanges?: (hasUnsavedChanges: boolean) => void
  columnHasNonNullCells: boolean
  columnHasNullCells: boolean
  typeChangeBlockedByCompositeKeys?: string[]
  relatedActionSteps?: RelatedActionSteps
  onSubmit?: (
    values: ColumnFormValues,
    {
      setSubmitting,
      resetForm,
    }: Pick<FormikHelpers<ColumnFormValues>, 'setSubmitting' | 'resetForm'>,
  ) => void
}

enum FieldTabType {
  GENERAL = 'general',
  PERMISSIONS = 'permissions',
  DEPENDENCIES = 'dependencies',
}

type ColumnTypeDropdownOption = {
  type: DataType
  innerType: AllowedArrayInnerTypes | null
  humanReadable: string
}

// Have to ignore all tags so that the hotkeys work in the input fields
configure({
  ignoreEventsCondition: () => {
    return false
  },
  ignoreTags: ['input', 'select', 'textarea'],
})

/**
 * Renders the form to create or update a table column
 */
export function CreateUpdateTableColumnForm({
  editingColumn,
  table,
  parentTableId,
  order,
  setFormulaError,
  operation,
  setRightPanelState,
  formulaError,
  showButtons = true,
  setHasUnsavedChanges,
  columnHasNonNullCells,
  columnHasNullCells,
  typeChangeBlockedByCompositeKeys = [],
  relatedActionSteps,
  onSubmit,
}: CreateTableColumnFormProps) {
  const [selectedTab, setSelectedTab] = useState<FieldTabType>(FieldTabType.GENERAL)

  const { t } = useTypedTranslation()
  const formik = useFormikContext<ColumnFormValues>()
  const { organizationTables } = useTableColumnForm()
  const { isInternal } = useGlobalContext()

  // Convert organization tables to field references for formula builder
  const organizationTableColumns = useMemo(() => {
    return organizationTables.flatMap((orgTable) =>
      orgTable.columns.map((column) => ({
        ...column,
        table: orgTable,
      })),
    )
  }, [organizationTables])

  const availableFormulaFields = useMemo(() => {
    return tableColumnsToFieldReferences(organizationTableColumns)
  }, [organizationTableColumns])

  // Disable the save button if any form value is invalid, if the form is being submitted, and
  // if no changes have been made
  const disableSaveButton = useMemo(() => {
    return (
      !formik.isValid ||
      formik.isSubmitting ||
      !isNull(formulaError) ||
      (!!editingColumn && !checkFormikDirtyState(editingColumn, formik.values))
    )
  }, [formik.isValid, formik.isSubmitting, formulaError, editingColumn, formik.values])

  const lookupColumnGroups = useMemo(() => {
    return uniqBy(
      compact(table.columns.map((column) => column.lookupColumnGroup)),
      (group) => group.id,
    )
  }, [table])

  // Get the selected lookup table ID
  const lookupTableId = useMemo(() => {
    // If using existing group, get table from the group
    const selectedGroup = lookupColumnGroups.find(
      (group) => group.id === formik.values.lookupColumnGroupId,
    )
    return selectedGroup?.lookupTable.id || formik.values.lookupTableId
  }, [formik.values.lookupTableId, formik.values.lookupColumnGroupId, lookupColumnGroups])

  // For the lookup filter expression, the available fields are all the columns in the
  // lookup table and the columns in the current table, excluding the current column, all the columns
  // in the parent table and all the columns in the child tables
  const lookupTableColumns = useMemo(() => {
    const orgTable = organizationTables.find((t) => t.id === lookupTableId)
    if (!orgTable) {
      return []
    }

    return orgTable.columns.map((column) => ({
      ...column,
      table: orgTable,
    }))
  }, [organizationTables, lookupTableId])

  const thisTableColumns = useMemo(() => {
    const orgTable = organizationTables.find((t) => t.id === table.id)
    if (!orgTable) {
      return []
    }

    return orgTable.columns.map((column) => ({
      ...column,
      table: orgTable,
    }))
  }, [organizationTables, table.id])

  const parentTableColumns = useMemo(() => {
    if (!parentTableId) {
      return []
    }
    const orgTable = organizationTables.find((t) => t.id === parentTableId)
    if (!orgTable) {
      return []
    }
    return orgTable.columns.map((column) => ({
      ...column,
      table: orgTable,
    }))
  }, [organizationTables, parentTableId])

  const childTableColumns = useMemo(() => {
    return organizationTables
      .filter((t) => t.parentTable?.id === table.id)
      .flatMap((t) =>
        t.columns.map((column) => ({
          ...column,
          table: t,
        })),
      )
  }, [organizationTables, table.id])

  const availableLookupFilterFields = useMemo(() => {
    // De-duplicate in case where the lookup table is the same as the parent table
    const fields = uniqBy(
      [...lookupTableColumns, ...thisTableColumns, ...parentTableColumns, ...childTableColumns],
      (column) => column.id,
    )
    return tableColumnsToFieldReferences(fields)
  }, [lookupTableColumns, thisTableColumns, parentTableColumns, childTableColumns])

  const ref = useHotkeys<HTMLFormElement>(
    [localKeyMap.CONTROL_ENTER, localKeyMap.COMMAND_ENTER, localKeyMap.ENTER, localKeyMap.ESC],
    (event) => {
      if (event.key === localKeyMap.ESC) {
        formik.resetForm()
        if (setRightPanelState) {
          setRightPanelState(null)
        }

        return
      }

      if (event.key === localKeyMap.ENTER) {
        event.preventDefault()

        if (!event.ctrlKey && !event.metaKey) {
          return
        }

        formik.submitForm()
      }
    },
    { enableOnFormTags: true },
  )

  const hasData = useMemo(() => {
    return table.hasRows
  }, [table])

  const nextAvailableOrder = useMemo(() => {
    return getNextOrder(table.columns)
  }, [table.columns])

  // Check if column will be the first displayed column on the table
  // This is used for frontend validation only - the actual order is assigned by the backend
  const isFirstColumn = useMemo(() => {
    const currentLowestColumnOrder = minBy(table.columns, (column) => column.order)?.order ?? 1
    // Use the order passed into the provider or use the nextAvailableOrder
    const columnOrder = !isUndefined(order) ? order : nextAvailableOrder
    return columnOrder <= currentLowestColumnOrder
  }, [table, nextAvailableOrder, order])

  // Default value is required if creating a new required column for a table that has rows
  const defaultValueIsRequired = !formik.values.isNullable && hasData && !editingColumn?.id

  // Variables for type and inner type checks
  // Any formula subtype (FORMULA_STRING, FORMULA_DATE) — for behavior common to every formula
  // column (formula builder, forcing read-only, etc.) regardless of output type.
  const declaredType = formik.values.type
  const isFormulaType = isAnyFormulaType(declaredType)
  // Format options should be shown only once a formula output type is determined by parsing the formula string. This is because the output type can change as the user edits the formula, and we don't want to show a format section for a formula that doesn't have a known output type yet.
  const hasValidatedFormula = Boolean(formik.values.formulaString?.trim()) && !formulaError
  const formulaOutputType = hasValidatedFormula ? formik.values.formula?.outputType : undefined
  const knownFormulaOutputType =
    formulaOutputType ??
    (isAnyFormulaType(declaredType) && declaredType !== DataType.FORMULA
      ? getFormulaOutputType(declaredType)
      : undefined)
  const isNumericFormulaType =
    isAnyFormulaType(declaredType) && knownFormulaOutputType === FormulaValueType.NUMBER
  const isBooleanType = formik.values.type === DataType.BOOLEAN
  const isFileType =
    formik.values.type === DataType.FILE_URL || formik.values.innerType === DataType.FILE_URL
  const isUserType =
    formik.values.type === DataType.USER || formik.values.innerType === DataType.USER
  const isLookupType =
    formik.values.type === DataType.LOOKUP || formik.values.innerType === DataType.LOOKUP
  const isIdType = formik.values.type === DataType.ID
  const isEnumType =
    formik.values.type === DataType.ENUM || formik.values.innerType === DataType.ENUM
  const isNumberType = formik.values.type === DataType.NUMBER
  const isDateFormulaType =
    isAnyFormulaType(declaredType) &&
    (knownFormulaOutputType === FormulaValueType.DATE ||
      knownFormulaOutputType === FormulaValueType.DATE_TIME)
  const isDateType =
    formik.values.type === DataType.DATE ||
    formik.values.type === DataType.DATE_TIME ||
    isDateFormulaType
  const isIdTypeWithoutAutogeneratedIndex = isIdType && !formik.values.indexAutogenerate
  const isArrayType = formik.values.type === DataType.ARRAY

  // Create the options for the column type/innerType dropdown
  // Step 1: Explicitly handle the flatMap operation with a common return type
  const typeOptionsWithReadableNames: ColumnTypeDropdownOption[] = Object.values(DataType).flatMap(
    (dataType): ColumnTypeDropdownOption[] => {
      // Directly handle the ARRAY type with its inner types
      if (dataType === DataType.ARRAY) {
        return ALLOWED_ARRAY_INNER_TYPES.map((innerType) => ({
          type: DataType.ARRAY,
          innerType,
          humanReadable: getHumanReadableTableColumnDataType(t, DataType.ARRAY, innerType),
        }))
      } else {
        // For all other types, return a singular array with the type and null innerType
        return [
          {
            type: dataType,
            innerType: null,
            humanReadable: getHumanReadableTableColumnDataType(t, dataType, null),
          },
        ]
      }
    },
  )

  // Step 2: Filter and sort with correctly inferred types
  const typeOptionsSortedAndFiltered: ColumnTypeDropdownOption[] = typeOptionsWithReadableNames
    .filter((option) => {
      // For the first column, check if the data type is a valid first table column type.
      if (isFirstColumn && !isValidFirstTableColumnType(option.type)) {
        return false
      }
      // Formula subtypes (FORMULA_NUMBER, FORMULA_STRING, etc.) are derived from the parsed
      // formula's output type — the user always picks the single generic "Formula" option and the
      // subtype is set automatically at submit time. Without this filter every subtype would
      // appear as a duplicate "Formula" entry.
      if (isAnyFormulaType(option.type) && option.type !== DataType.FORMULA) {
        return false
      }
      return orderLookup(t).has(option.humanReadable)
    })
    // Sort the options based on the provided desired order
    .sort((a, b) => {
      const orderA = orderLookup(t).get(a.humanReadable) ?? ORDERED_OPTIONS.length
      const orderB = orderLookup(t).get(b.humanReadable) ?? ORDERED_OPTIONS.length
      return orderA - orderB
    })

  const typeOptionDescriptorToValue = (descriptor: ColumnTypeDropdownOption): string => {
    return `${descriptor.type}_${descriptor.innerType}`
  }

  const typeOptionKeyToDescriptor: Record<string, ColumnTypeDropdownOption> = Object.fromEntries(
    typeOptionsSortedAndFiltered.map((option) => [typeOptionDescriptorToValue(option), option]),
  )

  const typeSelections: Option[] = typeOptionsSortedAndFiltered.map((option) => ({
    value: typeOptionDescriptorToValue(option),
    label: option.humanReadable,
    startIcon: getIconByColumnType(option.type, option.innerType),
  }))

  const typeTooltip = () => {
    if (columnHasNonNullCells) {
      return t('table.create_column.cannot_update_has_data')
    }
    if (typeChangeBlockedByCompositeKeys.length > 0) {
      return isInternal
        ? t('table.create_column.cannot_update_composite_keys', {
            names: typeChangeBlockedByCompositeKeys.join(', '),
          })
        : t('table.create_column.cannot_update_composite_keys_contact_support', {
            names: typeChangeBlockedByCompositeKeys.join(', '),
          })
    }
    return undefined
  }
  const typeChangeDisabled = columnHasNonNullCells || typeChangeBlockedByCompositeKeys.length > 0
  const fields: CreateUpdateDialogField[] = [
    {
      element: (
        <ShortTextInput
          autoFocus={!editingColumn || editingColumn.name === DEFAULT_COLUMN_VALUES.name}
          errorText={(formik.touched.name && formik.errors.name) || undefined}
          label={t('table.create_column.name')}
          name="name"
          onBlur={() => formik.setFieldTouched('name', true)}
          onChange={(event) => formik.setFieldValue('name', event.target.value)}
          value={formik.values.name}
        />
      ),
    },
    {
      element: (
        <LongTextInput
          errorText={(formik.touched.description && formik.errors.description) || undefined}
          label={t('table.create_column.description')}
          name="description"
          onBlur={() => formik.setFieldTouched('description', true)}
          onChange={(event) => formik.setFieldValue('description', event.target.value)}
          value={formik.values.description ?? ''}
        />
      ),
    },
    {
      element: (
        <Dropdown
          disabled={typeChangeDisabled}
          label={t('table.create_column.type')}
          onChange={(newValue) => {
            if (!newValue) {
              return
            }
            const descriptor = typeOptionKeyToDescriptor[newValue]
            formik.setValues({
              ...formik.values,
              type: descriptor.type,
              innerType: descriptor.innerType as DataType | null,
            })
          }}
          options={typeSelections}
          showClearButton={false}
          startAdornment={getIconByColumnType(
            formik.values.type,
            formik.values.innerType as AllowedArrayInnerTypes | null,
          )}
          tooltip={typeTooltip()}
          value={typeOptionDescriptorToValue({
            type: formik.values.type,
            innerType: formik.values.innerType as AllowedArrayInnerTypes | null,
            humanReadable: getHumanReadableTableColumnDataType(
              t,
              formik.values.type,
              isValidArrayInnerType(formik.values.innerType) ? formik.values.innerType : null,
            ),
          })}
        />
      ),
    },
    {
      element: (
        <Tooltip open={formik.values.indexAutogenerate || isFormulaType ? undefined : false}>
          <TooltipTrigger asChild>
            <span>
              <Checkbox
                checked={formik.values.isReadOnly}
                // Disable the checkbox if index autogenerate is true or type is FORMULA since those
                // are always read-only
                disabled={formik.values.indexAutogenerate || isFormulaType}
                label={t('table.create_column.read_only')}
                name="isReadOnly"
                onCheckedChange={() =>
                  formik.setFieldValue('isReadOnly', !formik.values.isReadOnly)
                }
              />
            </span>
          </TooltipTrigger>
          <TooltipContent>{t('table.create_column.cannot_make_read_only')}</TooltipContent>
        </Tooltip>
      ),
    },
  ]

  // Exact decimal: a NUMBER column workflows-as-code reads and writes as a Decimal. Storage is
  // already exact, so the flag flips freely; everywhere else the column stays an ordinary number.
  if (isNumberType) {
    fields.push({
      element: (
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Checkbox
                checked={formik.values.isDecimal}
                label={t('table.create_column.exact_decimal')}
                name="isDecimal"
                onCheckedChange={() => formik.setFieldValue('isDecimal', !formik.values.isDecimal)}
              />
            </span>
          </TooltipTrigger>
          <TooltipContent>{t('table.create_column.exact_decimal_help')}</TooltipContent>
        </Tooltip>
      ),
    })
  }

  if (isFormulaType) {
    fields.push({
      element: (
        <FormulaBuilder
          allowMultiColumnAggregations
          availableFields={availableFormulaFields}
          currentContextId={table.id}
          excludeFieldId={editingColumn?.id || editingColumn?.tempId}
          label={t('table.create_column.formula')}
          onChange={(formulaString, parsedFormula) => {
            formik.setValues(
              (prevValues) => ({
                ...prevValues,
                isReadOnly: true,
                formulaString,
                // Safe cast: CreateUpdateTableColumnForm always uses 'formula' scope, not 'logicalExpression'
                formula: parsedFormula as FormulaInput | null,
              }),
              false, // Do not validate on change
            )
          }}
          onError={setFormulaError}
          onSubmit={formik.handleSubmit}
          parentContextId={parentTableId}
          value={formik.values.formulaString ?? ''}
        />
      ),
    })
  }

  // Formula and boolean fields cannot be nullable so don't allow the user to configure it
  // If table already has data, cannot set is required at table column creation for FILE_URL type
  // or inner type or ID type or LOOKUP type or inner type since user cannot enter default value
  // for those types.
  // ID type columns with autogenerated index can still be required since a value is generated
  // for each row
  const hasDataConflict =
    !editingColumn?.id &&
    hasData &&
    (isFileType || isUserType || isIdTypeWithoutAutogeneratedIndex || isLookupType)
  const canMakeRequired = !isFormulaType && !isBooleanType && !hasDataConflict

  // Always show the checkbox if the column is currently required unless it is a boolean
  const canMakeOptional = !isBooleanType && !formik.values.isNullable

  if (canMakeOptional || canMakeRequired) {
    // Disable the button if currently column is nullable and there are existing cells
    // with null value
    const disableMakeRequiredButton = formik.values.isNullable && columnHasNullCells
    fields.push({
      element: (
        <Tooltip open={disableMakeRequiredButton ? undefined : false}>
          <TooltipTrigger asChild>
            <span>
              <Checkbox
                checked={!formik.values.isNullable}
                // Disable the checkbox if the existing column has cells with null value
                disabled={disableMakeRequiredButton}
                label={t('table.create_column.required')}
                name="isNullable"
                onCheckedChange={() =>
                  formik.setFieldValue('isNullable', !formik.values.isNullable)
                } // Invert the value
              />
            </span>
          </TooltipTrigger>
          <TooltipContent>{t(`table.create_column.cannot_make_required`)}</TooltipContent>
        </Tooltip>
      ),
    })
  }

  if (isIdType) {
    // ID fields require the user to fill out an index type and optionally the prefix/suffix
    fields.push(
      {
        element: (
          <Dropdown
            disabled={columnHasNonNullCells}
            label={t('table.create_column.index_type')}
            onChange={(newValue) => {
              newValue && formik.setFieldValue('indexType', newValue, false)
            }}
            options={Object.values(IndexType)
              .filter((indexType) => indexType !== IndexType.NONE)
              .map((indexType) => ({
                value: indexType,
                label: titleCase(indexType.toString()),
              }))}
            showClearButton={false}
            tooltip={
              columnHasNonNullCells ? t('table.create_column.cannot_update_index_type') : undefined
            }
            value={formik.values.indexType}
          />
        ),
      },
      {
        element: (
          <ShortTextInput
            disabled={columnHasNonNullCells}
            label={t('table.create_column.index_prefix')}
            name="indexPrefix"
            onBlur={() => formik.setFieldTouched('indexPrefix', true)}
            onChange={(event) => formik.setFieldValue('indexPrefix', event.target.value)}
            tooltip={
              columnHasNonNullCells
                ? t('table.create_column.cannot_update_index_prefix')
                : undefined
            }
            value={formik.values.indexPrefix}
          />
        ),
      },
      {
        element: (
          <ShortTextInput
            disabled={columnHasNonNullCells}
            label={t('table.create_column.index_suffix')}
            name="indexSuffix"
            onBlur={() => formik.setFieldTouched('indexSuffix', true)}
            onChange={(event) => formik.setFieldValue('indexSuffix', event.target.value)}
            tooltip={
              columnHasNonNullCells
                ? t('table.create_column.cannot_update_index_suffix')
                : undefined
            }
            value={formik.values.indexSuffix}
          />
        ),
      },
    )

    if (formik.values.indexType === IndexType.NUMBER) {
      // For number index fields, the autogenerate setting is available
      fields.push({
        element: (
          <Tooltip open={columnHasNonNullCells ? undefined : false}>
            <TooltipTrigger asChild>
              <span>
                <Checkbox
                  checked={formik.values.indexAutogenerate}
                  disabled={columnHasNonNullCells}
                  label={t('table.create_column.index_autogenerate')}
                  name="indexAutogenerate"
                  onBlur={() => formik.setFieldTouched('indexAutogenerate', true)}
                  onCheckedChange={(checked) => formik.setFieldValue('indexAutogenerate', checked)}
                />
              </span>
            </TooltipTrigger>
            <TooltipContent>{t('table.create_column.cannot_update_has_data')}</TooltipContent>
          </Tooltip>
        ),
      })
    }
  }
  if (isEnumType) {
    const optionsError =
      typeof formik.errors.enumOptionInputs === 'string'
        ? formik.errors.enumOptionInputs
        : undefined

    // Enum fields require the user to fill out the enum options
    fields.push({
      element: (
        <InputWrapper
          errorText={(formik.touched.enumOptionInputs && optionsError) || undefined}
          label={t('table.create_column.options')}
        >
          <EnumOptionsEditor
            isMultiSelect={isArrayType}
            onChange={(options) => {
              formik.setFieldValue('enumOptionInputs', options)
              formik.setFieldValue('enumOptions', enumOptionLabels(options))
              formik.setFieldTouched('enumOptionInputs', true)
            }}
            options={formik.values.enumOptionInputs ?? []}
          />
        </InputWrapper>
      ),
    })
  }
  if (isFileType) {
    fields.push({
      element: (
        <Checkbox
          checked={formik.values.displayImagesInline}
          label={t('table.create_column.display_images_inline')}
          name="displayImagesInline"
          onBlur={() => formik.setFieldTouched('displayImagesInline', true)}
          onCheckedChange={(checked) => formik.setFieldValue('displayImagesInline', checked)}
        />
      ),
    })
  }
  if (isNumberType || isNumericFormulaType) {
    fields.push({
      element: (
        <>
          <Divider />
          <NumberFormatSection />
        </>
      ),
    })
  }
  if (isDateType) {
    fields.push({
      element: (
        <>
          <Divider />
          <DateFormatSection />
        </>
      ),
    })
  }

  // Let the user set a default value if creating certain types of column types if the existing table
  // already has rows.  The default value should be required if the column is required
  // User cannot select a default value for several types so don't show in those cases: FILE, USER,
  // ID, FORMULA, LOOKUP
  const canSetDefaultValue =
    hasData &&
    !editingColumn?.id &&
    !isFileType &&
    !isUserType &&
    !isIdType &&
    !isFormulaType &&
    !isLookupType

  if (canSetDefaultValue) {
    fields.push({
      element: (
        <DefaultValueField<DefaultValueContext.TABLE_COLUMN>
          context={DefaultValueContext.TABLE_COLUMN}
          defaultValue={formik.values.defaultValue || null}
          defaultValueIsRequired={defaultValueIsRequired}
          enumOptions={formik.values.enumOptions || []}
          label={t('table.create_column.backfill_data')}
          setDefaultValue={(defaultValue) => formik.setFieldValue('defaultValue', defaultValue)}
          tableColumnInnerType={formik.values.innerType as AllowedArrayInnerTypes | null}
          tableColumnType={formik.values.type}
        />
      ),
    })
  }

  if (isLookupType) {
    fields.push(
      ...[
        {
          element: (
            <LookupColumnFields
              columnHasNonNullCells={columnHasNonNullCells}
              isArray={isArrayType}
              lookupColumnGroups={lookupColumnGroups}
              onSubmit={onSubmit}
              tableId={table.id}
            />
          ),
        },
        {
          element: (
            <FormulaBuilder
              availableFields={availableLookupFilterFields}
              excludeFieldId={editingColumn?.id || editingColumn?.tempId}
              label={t('lookup.lookup_filter_condition')}
              languageScope="logicalExpression"
              onChange={(formulaString, parsedFormula) => {
                formik.setValues(
                  (prevValues) => ({
                    ...prevValues,
                    isReadOnly: true,
                    lookupFilterExpressionString: formulaString,
                    lookupFilterExpression: parsedFormula as LogicExpressionInput | null,
                  }),
                  false, // Do not validate on change
                )
              }}
              onError={setFormulaError}
              onSubmit={formik.handleSubmit}
              value={formik.values.lookupFilterExpressionString ?? ''}
            />
          ),
        },
      ],
    )
  }

  // Based on changes in column type, toggle on/off what the acceptable/unacceptable options are
  useEffect(() => {
    formik.setValues((currentValues) => {
      let updates: ColumnFormValues = { ...currentValues }
      // If the type is not ID, set the index type to NONE, set indexAutogenerate to false and clear
      // any index prefix or suffix
      if (!isIdType) {
        updates = {
          ...updates,
          indexType: IndexType.NONE,
          indexAutogenerate: false,
          indexPrefix: '',
          indexSuffix: '',
        }
      } else {
        // If the type is changed to ID, change the index type to NUMBER because index type NONE
        // is not an option for ID type unless the existing column is already of type ID, then
        // just use the current value
        updates = {
          ...updates,
          indexType:
            currentValues.indexType === IndexType.NONE ? IndexType.NUMBER : currentValues.indexType,
        }
        // If index autogenerate is true, set isReadOnly to true otherwise, set isReadOnly to false
        if (!isIdTypeWithoutAutogeneratedIndex) {
          updates = { ...updates, isReadOnly: true }
        }
      }

      // Set read only to true if the type of the column is FORMULA
      if (isFormulaType) {
        updates = { ...updates, isReadOnly: true }
      } else {
        // Set formula error to null when the type is changed to something other than Formula
        setFormulaError(null)
        // If not a formula type, make sure to clear out any formula input or formula string
        updates = { ...updates, formula: null, formulaString: undefined }
      }

      // If the type is not ENUM, clear the options and their label mirror
      if (!isEnumType) {
        updates = { ...updates, enumOptions: [], enumOptionInputs: [] }
      }

      // Force optional when the type can't backfill a value for existing rows on a new column
      // (ID w/o autogen, file, user, lookup). Gated by `hasDataConflict` so it applies only to new
      // columns — existing columns keep their persisted `isNullable` when the panel opens.
      if (hasDataConflict) {
        updates = { ...updates, isNullable: true }
      }

      if (!isFileType) {
        updates = { ...updates, displayImagesInline: false }
      }

      // Adjust the default value for transitions between array and non-array types.
      if (isArrayType) {
        updates = { ...updates, defaultValue: { type: DossDataFormat.ARRAY, data: [] } }
      } else {
        updates = { ...updates, defaultValue: null }
      }

      // If the type is not lookup, set lookupColumnId, lookupColumnGroupId, and lookupTableId to null
      if (!isLookupType) {
        updates = {
          ...updates,
          lookupColumnId: null,
          lookupColumnGroupId: null,
          lookupTableId: null,
          lookupFilterExpressionString: undefined,
          lookupFilterExpression: null,
        }
      }

      if (isBooleanType) {
        const defaultFalse: BooleanCellData = { type: DossDataFormat.BOOLEAN, data: false }
        updates = { ...updates, isNullable: false, defaultValue: defaultFalse }
      }

      // Reset formatting outside supported types; initialize defaults when changing format families.
      if (!isNumberType && !isNumericFormulaType && !isDateType) {
        updates = { ...updates, formatPreset: null, formatOptions: null }
      } else if (isDateType && updates.formatPreset !== FormatPreset.DATE_CUSTOM) {
        updates = {
          ...updates,
          formatPreset: FormatPreset.DATE_CUSTOM,
          formatOptions: DEFAULT_FORMAT_OPTIONS[FormatPreset.DATE_CUSTOM],
        }
      } else if (
        (isNumberType || isNumericFormulaType) &&
        (!updates.formatPreset || updates.formatPreset === FormatPreset.DATE_CUSTOM)
      ) {
        updates = {
          ...updates,
          formatPreset: FormatPreset.NUMBER_PLAIN,
          formatOptions: DEFAULT_FORMAT_OPTIONS[FormatPreset.NUMBER_PLAIN],
        }
      }

      return updates
    })
    // Adding formik to the dependency array causes Maximum update depth exceeded error
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isArrayType,
    isEnumType,
    isFileType,
    isUserType,
    isFormulaType,
    isNumericFormulaType,
    isBooleanType,
    isIdType,
    isLookupType,
    isNumberType,
    isDateType,
    hasData,
    hasDataConflict,
    setFormulaError,
    isIdTypeWithoutAutogeneratedIndex,
  ])

  useEffect(() => {
    if (setHasUnsavedChanges) {
      const initialValues = editingColumn ?? DEFAULT_COLUMN_VALUES
      const unsavedChanges = checkFormikDirtyState(initialValues, formik.values)
      setHasUnsavedChanges(unsavedChanges)
    }
  }, [setHasUnsavedChanges, editingColumn, formik.values])

  let tooltipTitle = uniq(Object.values(formik.errors)).join(', ')
  if (formulaError) {
    if (tooltipTitle) {
      tooltipTitle += ', '
    }
    tooltipTitle += formulaError
  }

  const showPermissionsTab = editingColumn?.id !== undefined
  const showDependenciesTab = operation === RightPanelOperation.UPDATE && isInternal
  const showTabs = showPermissionsTab || showDependenciesTab

  return (
    <div className="flex-1 overflow-hidden">
      {showTabs ? (
        <Tabs
          className="flex h-full flex-col overflow-hidden"
          onValueChange={(value) => setSelectedTab(value as FieldTabType)}
          value={selectedTab}
        >
          {/* overflow-x-auto forces overflow-y to clip; TabsTrigger uses -bottom-[1px] for the
              active underline — extra bottom padding keeps border-b-2 visible */}
          <div className="shrink-0 overflow-x-auto px-4 pb-1">
            <TabsList className="min-w-max justify-start">
              <TabsTrigger className="shrink-0 px-2" value={FieldTabType.GENERAL}>
                {t('table.column_dependencies.general_tab')}
              </TabsTrigger>
              {showPermissionsTab && (
                <TabsTrigger className="shrink-0 px-2" value={FieldTabType.PERMISSIONS}>
                  {t('table.column_dependencies.permissions_tab')}
                </TabsTrigger>
              )}
              {showDependenciesTab && (
                <TabsTrigger className="shrink-0 px-2" value={FieldTabType.DEPENDENCIES}>
                  {t('table.column_dependencies.dependencies_tab')}
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          <TabsContent className="m-0 h-full overflow-hidden" value={FieldTabType.GENERAL}>
            <form
              ref={ref}
              className="flex size-full flex-col overflow-hidden"
              onSubmit={formik.handleSubmit}
              tabIndex={-1}
            >
              <div className="flex-1 overflow-y-auto p-4">
                <div className="flex flex-col gap-2">
                  {fields.map((field, index) => (
                    <div key={`column-form-${index}`} className="flex flex-col gap-1">
                      {field.element}
                    </div>
                  ))}
                </div>
              </div>
              {showButtons && (
                <div className="flex items-center justify-between border-t border-gray-4 p-4">
                  <Button
                    onClick={() => {
                      if (setRightPanelState) {
                        setRightPanelState(null)
                      }
                      formik.resetForm()
                    }}
                    type="button"
                    variant="outline"
                  >
                    {t('common.actions.cancel')}
                  </Button>
                  <Button disabled={disableSaveButton} type="submit">
                    {t('common.actions.save')} ⌘↵
                  </Button>
                </div>
              )}
            </form>
          </TabsContent>

          {editingColumn && editingColumn.id && (
            <TabsContent
              className="m-0 h-full overflow-hidden pt-2"
              value={FieldTabType.PERMISSIONS}
            >
              <FieldPermissionsTab
                setRightPanelState={setRightPanelState}
                tableColumn={editingColumn}
                tableId={table.id}
              />
            </TabsContent>
          )}

          {showDependenciesTab && (
            <TabsContent className="m-0 h-full overflow-hidden" value={FieldTabType.DEPENDENCIES}>
              <div className="flex h-full flex-col gap-6 overflow-y-auto p-4">
                <OpenDependencyExplorerButton
                  className="mx-1 mt-2 self-start"
                  entityId={editingColumn?.id}
                  entityType={ComprehensionEntityType.TABLE_COLUMN}
                />
                {!relatedActionSteps ? (
                  <div className="flex items-center justify-center pt-8">
                    <LoadingSpinner type="navigation" />
                  </div>
                ) : (
                  <RelatedActionStepsView
                    entityName={t('table.column_dependencies.column')}
                    relatedActionSteps={relatedActionSteps}
                    writeStepsDescription={t('table.column_dependencies.write_steps_description')}
                    writeStepsTitle={t('table.column_dependencies.write_steps')}
                  />
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      ) : (
        <form
          ref={ref}
          className="flex size-full flex-col overflow-hidden"
          onSubmit={formik.handleSubmit}
          tabIndex={-1}
        >
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex flex-col gap-2">
              {fields.map((field, index) => (
                <div key={`column-form-${index}`} className="flex flex-col gap-1">
                  {field.element}
                </div>
              ))}
            </div>
          </div>
          {showButtons && (
            <div className="flex items-center justify-between border-t border-gray-4 p-4">
              <Button
                onClick={() => {
                  if (setRightPanelState) {
                    setRightPanelState(null)
                  }
                  formik.resetForm()
                }}
                type="button"
                variant="outline"
              >
                {t('common.actions.cancel')}
              </Button>
              <Button disabled={disableSaveButton} type="submit">
                {t('common.actions.save')} ⌘↵
              </Button>
            </div>
          )}
        </form>
      )}
    </div>
  )
}
